import { useState, useEffect } from 'react';
import { apiClient } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AppPageIntro from '@/components/AppPageIntro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calendar,
  Car,
  FileText,
  Refrigerator,
  Clock,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Plus,
  Pencil,
  Trash2,
  Save,
  X
} from 'lucide-react';

const eventTypeConfig = {
  appliance: {
    icon: Refrigerator,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    label: 'Apparaat'
  },
  car: {
    icon: Car,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    label: 'Auto'
  },
  document: {
    icon: FileText,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    label: 'Document'
  },
  passport: {
    icon: FileText,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    label: 'Paspoort'
  }
};

const urgencyConfig = {
  high: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Nu regelen' },
  medium: { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Binnenkort' },
  low: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Op schema' }
};

const TimelineCard = ({ event }) => {
  const config = eventTypeConfig[event.type] || eventTypeConfig.document;
  const urgency = urgencyConfig[event.urgency] || urgencyConfig.medium;
  const Icon = config.icon;

  return (
    <Card className={`shadow-card hover:shadow-card-hover transition-all border-l-4 ${
      event.urgency === 'high' ? 'border-l-red-500' : 
      event.urgency === 'medium' ? 'border-l-orange-500' : 'border-l-green-500'
    }`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-6 h-6 ${config.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-heading font-semibold truncate">{event.name}</h3>
              <Badge className={urgency.color}>{urgency.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{event.notes}</p>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{event.days_until_action} dagen</span>
              </div>
              {event.estimated_value && (
                <div className="text-sm text-muted-foreground">
                  Waarde: <span className="font-mono">€{Number(event.estimated_value || 0).toLocaleString('nl-NL')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const eventTypes = [
  { value: 'appliance', label: 'Apparaat' },
  { value: 'car', label: 'Auto' },
  { value: 'document', label: 'Document' },
  { value: 'passport', label: 'Paspoort' },
];

const AllEventsCard = ({ event, onEdit, onDelete }) => {
  const config = eventTypeConfig[event.type] || eventTypeConfig.document;
  const Icon = config.icon;

  return (
    <div className="flex items-center justify-between p-4 rounded-xl border border-border hover:border-primary/20 hover:bg-muted/50 transition-all">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div>
          <p className="font-medium">{event.name}</p>
          <p className="text-sm text-muted-foreground">{config.label}</p>
        </div>
      </div>
      <div className="text-right">
        {event.expiry_date && (
          <p className="text-sm">
            Verloopt: {new Date(event.expiry_date).toLocaleDateString('nl-NL')}
          </p>
        )}
        {event.type === 'passport' && (
          <>
            {event.passport_number && (
              <p className="text-xs text-muted-foreground">Paspoortnr: {event.passport_number}</p>
            )}
            {event.bsn && (
              <p className="text-xs text-muted-foreground">BSN: {event.bsn}</p>
            )}
            {event.issue_date && (
              <p className="text-xs text-muted-foreground">
                Uitgifte: {new Date(event.issue_date).toLocaleDateString('nl-NL')}
              </p>
            )}
          </>
        )}
        {event.estimated_value && (
          <p className="text-sm text-muted-foreground">
            €{Number(event.estimated_value || 0).toLocaleString('nl-NL')}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button size="sm" variant="secondary" onClick={() => onEdit(event)}>
            <Pencil className="w-4 h-4 mr-1" />
            Wijzig
          </Button>
          <Button size="sm" variant="destructive" onClick={() => onDelete(event.id)}>
            <Trash2 className="w-4 h-4 mr-1" />
            Verwijder
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function LifePlanning() {
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    type: 'appliance',
    name: '',
    next_action_date: '',
    expiry_date: '',
    estimated_value: '',
    notes: '',
    passport_number: '',
    bsn: '',
    issue_date: '',
    issuing_authority: '',
    nationality: '',
    birth_date: '',
    birth_place: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, upcomingRes] = await Promise.all([
        apiClient.get('/life/events'),
        apiClient.get('/life/upcoming')
      ]);
      setEvents(eventsRes.data);
      setUpcomingEvents(upcomingRes.data);
    } catch (error) {
      console.error('Error fetching life events:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      type: 'appliance',
      name: '',
      next_action_date: '',
      expiry_date: '',
      estimated_value: '',
      notes: '',
      passport_number: '',
      bsn: '',
      issue_date: '',
      issuing_authority: '',
      nationality: '',
      birth_date: '',
      birth_place: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      next_action_date: form.next_action_date ? new Date(form.next_action_date).toISOString() : null,
      expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : null,
      issue_date: form.issue_date ? new Date(form.issue_date).toISOString() : null,
      birth_date: form.birth_date || null,
      estimated_value: form.estimated_value === '' ? null : Number(form.estimated_value),
      purchase_date: null,
    };
    if (form.type === 'passport') {
      const required = ['passport_number', 'bsn', 'issue_date', 'expiry_date', 'nationality'];
      const missing = required.filter((field) => !payload[field]);
      if (missing.length > 0) {
        alert(`Vul deze paspoortvelden in: ${missing.join(', ')}`);
        return;
      }
    }

    try {
      if (editingId) {
        await apiClient.put(`/life/events/${editingId}`, payload);
      } else {
        await apiClient.post('/life/events', payload);
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving life event:', error);
    }
  };

  const startEdit = (event) => {
    setEditingId(event.id);
    setForm({
      type: event.type,
      name: event.name,
      next_action_date: event.next_action_date ? new Date(event.next_action_date).toISOString().split('T')[0] : '',
      expiry_date: event.expiry_date ? new Date(event.expiry_date).toISOString().split('T')[0] : '',
      estimated_value: event.estimated_value ?? '',
      notes: event.notes || '',
      passport_number: event.passport_number || '',
      bsn: event.bsn || '',
      issue_date: event.issue_date ? new Date(event.issue_date).toISOString().split('T')[0] : '',
      issuing_authority: event.issuing_authority || '',
      nationality: event.nationality || '',
      birth_date: event.birth_date || '',
      birth_place: event.birth_place || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Item verwijderen?')) return;
    try {
      await apiClient.delete(`/life/events/${eventId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting life event:', error);
    }
  };

  const urgentCount = upcomingEvents.filter(e => e.urgency === 'high').length;
  const totalValue = events.reduce((sum, e) => sum + (e.estimated_value || 0), 0);
  const hasEvents = events.length > 0;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-40 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="life-planning-page">
      <AppPageIntro
        eyebrow="Belangrijke momenten"
        title="Levensplanning"
        description="Bewaar belangrijke spullen, documenten en datums op een vaste plek zodat je ook onderweg snel ziet wat aandacht nodig heeft."
        badges={['Documenten', 'Aflopende datums', 'Acties']}
        action={
          <Button className="rounded-full" variant="outline" onClick={resetForm}>
            <Plus className="w-4 h-4 mr-2" />
            Nieuw item
          </Button>
        }
      />

      {!hasEvents && (
        <Card className="shadow-card border-primary/15 bg-primary/5">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Begin met je levensplanning
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 lg:grid-cols-3">
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">1. Voeg een belangrijk item toe</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Denk aan een auto, paspoort, apparaat of ander document dat je wilt bijhouden.
              </p>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">2. Zet een datum klaar</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Vul een afloopdatum of volgende actiedatum in, zodat je later op tijd wordt herinnerd.
              </p>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">3. Voeg een korte notitie toe</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Zo weet je later sneller waarom iets belangrijk is of wat je nog moet regelen.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="font-heading">{editingId ? 'Item wijzigen' : 'Item toevoegen'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select value={form.type} onValueChange={(value) => setForm((p) => ({ ...p, type: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Naam"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <Input
              type="date"
              value={form.next_action_date}
              onChange={(e) => setForm((p) => ({ ...p, next_action_date: e.target.value }))}
            />
            <Input
              type="date"
              value={form.expiry_date}
              onChange={(e) => setForm((p) => ({ ...p, expiry_date: e.target.value }))}
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Geschatte waarde"
              value={form.estimated_value}
              onChange={(e) => setForm((p) => ({ ...p, estimated_value: e.target.value }))}
            />
            <Textarea
              placeholder="Notities"
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              className="md:col-span-2"
            />
            {form.type === 'passport' && (
              <>
                <Input
                  placeholder="Paspoortnummer"
                  value={form.passport_number}
                  onChange={(e) => setForm((p) => ({ ...p, passport_number: e.target.value }))}
                  required
                />
                <Input
                  placeholder="BSN"
                  value={form.bsn}
                  onChange={(e) => setForm((p) => ({ ...p, bsn: e.target.value }))}
                  required
                />
                <Input
                  type="date"
                  value={form.issue_date}
                  onChange={(e) => setForm((p) => ({ ...p, issue_date: e.target.value }))}
                  required
                />
                <Input
                  placeholder="Uitgevende instantie"
                  value={form.issuing_authority}
                  onChange={(e) => setForm((p) => ({ ...p, issuing_authority: e.target.value }))}
                />
                <Input
                  placeholder="Nationaliteit"
                  value={form.nationality}
                  onChange={(e) => setForm((p) => ({ ...p, nationality: e.target.value }))}
                  required
                />
                <Input
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => setForm((p) => ({ ...p, birth_date: e.target.value }))}
                />
                <Input
                  placeholder="Geboorteplaats"
                  value={form.birth_place}
                  onChange={(e) => setForm((p) => ({ ...p, birth_place: e.target.value }))}
                  className="md:col-span-2"
                />
              </>
            )}
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" className="rounded-full">
                {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {editingId ? 'Opslaan' : 'Toevoegen'}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" className="rounded-full" onClick={resetForm}>
                  <X className="w-4 h-4 mr-2" />
                  Annuleren
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totaal items</p>
                <p className="font-mono text-2xl font-bold">{events.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nu regelen</p>
                <p className="font-mono text-2xl font-bold text-red-600">{urgentCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Komende 90 dagen</p>
                <p className="font-mono text-2xl font-bold">{upcomingEvents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Geschatte waarde</p>
                <p className="font-mono text-2xl font-bold">€{totalValue.toLocaleString('nl-NL')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Timeline */}
      <div>
        <h2 className="font-heading text-xl font-semibold mb-4">Komende acties</h2>
        {upcomingEvents.length === 0 ? (
          <Card className="shadow-card">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <p className="text-muted-foreground">Er staat de komende 90 dagen niets op je lijst dat direct aandacht nodig heeft.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcomingEvents.map((event) => (
              <TimelineCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>

      {/* All Items */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="font-heading">Alles wat je bewaart</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {events.length === 0 ? (
            <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
              Je hebt nog niets toegevoegd. Begin hierboven met een eerste document, apparaat of andere belangrijke herinnering.
            </div>
          ) : (
            events.map((event) => (
              <AllEventsCard key={event.id} event={event} onEdit={startEdit} onDelete={deleteEvent} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
